<template>
  <div class="comments" :class="{ minimize: isMinimize }" v-resize="onResize">
    <div
      class="comments--head"
      @click="toggleMinimize"
      :class="{ minimized: isMinimize }"
    >
      <div
        class="d-flex align-center"
        :class="isMinimize ? 'flex-column' : 'pb-1 pt-1'"
      >
        <div class="font-weight-bold" v-if="smallTitle">Comments</div>
        <div v-else-if="!isMinimize">
          <h2>Comments</h2>
        </div>
        <v-chip
          color="primary"
          small
          :class="{ 'count-minimized': isMinimize }"
        >
          {{ comments | count }}
        </v-chip>
        <v-icon v-if="isMinimize" large>mdi-comment</v-icon>
      </div>
      <v-btn text icon v-show="minimizable && !isMinimize">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <div v-if="isLoading">
      <v-skeleton-loader
        type="list-item-avatar-three-line"
        v-for="(comments, index) in skeletonComments"
        :key="index"
      />
    </div>
    <div class="comments--body" v-show="!isMinimize" v-else>
      <div class="comments__items">
        <div v-for="(comments, index) in commentsByDate" :key="index">
          <p class="comments__item__date">
            {{ comments[0].createdAt | timeForHumans }}
          </p>
          <div
            class="comments__item"
            :class="{ 'comments__item--self': isSelf(comment.author.id) }"
            v-for="(comment, index) in comments"
            :key="index"
          >
            <v-avatar
              size="36px"
              v-if="!isSelf(comment.author.id)"
              color="grey lighten-4"
            >
              <span class="grey--text">
                {{ comment.author | initials }}
              </span>
            </v-avatar>
            <div class="comments__item__message">
              <div
                class="comments__item__message__author"
                v-if="!isSelf(comment.author.id)"
              >
                {{ comment.author.firstName }} {{ comment.author.lastName }}
              </div>
              <v-textarea
                aria-label="Comment"
                row-height="1"
                auto-grow
                readonly
                rounded
                :value="comment.body"
              />
              <div v-if="size(comment.attachments)">
                <FileComponent
                  class="mb-5"
                  v-for="attachment in comment.attachments"
                  :key="attachment.id"
                  :file="attachment"
                  @show="showFile(attachment)"
                  customActions
                  disabled
                  :showFileName="!isMobile"
                />
              </div>
              <PDFViewFull :data="fileDialog" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-text-field
      :disabled="isAdding || isLoading"
      v-show="!isMinimize"
      v-if="!readOnly"
      v-model="newComment"
      class="comments-input"
      color="primary"
      ref="commentField"
      aria-label="Add comment"
      placeholder="Add comment..."
      append-icon="mdi-arrow-up-circle"
      @click:append="addComment"
      @keydown.enter="addComment"
      :loading="isAdding"
    />
    <FileUpload
      v-if="withAttachments"
      class="mt-2"
      @unselectFile="unselectFile"
      @selectFile="selectFile"
      :files="files"
      :fileTypes="['application/pdf']"
      disableAutoProceed
      customActions
      isMultiple
    />
  </div>
</template>

<script>
import {
  getNameInitials,
  getTimeForHumans,
  groupByDate
} from "@/utils/helpers";
import { createNamespacedHelpers } from "vuex";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import FileComponent from "@/components/common/FileComponent";
import PDFViewFull from "@/components/common/PDFViewFull";
import { size } from "lodash";
import { getToken } from "@/services/utils";
import FileUpload from "@/components/common/FileUpload";

const { mapState } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  components: { FileComponent, PDFViewFull, FileUpload },
  props: {
    /**
     * An array of objects for comments
     */
    files: {
      type: Array,
      required: false
    },
    comments: {
      type: Array,
      required: false,
      default: null
    },
    /**
     * Boolean for read only
     */
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Boolean to allow minimize comment
     */
    minimizable: {
      type: Boolean,
      required: false,
      default: true
    },
    isLoading: {
      type: Boolean,
      required: false
    },
    isAdding: {
      type: Boolean,
      required: false
    },
    focusOnInit: Boolean,
    withAttachments: Boolean,
    smallTitle: Boolean
  },
  mounted() {
    if (this.focusOnInit && !size(this.comments)) {
      this.focusOnTextField();
    }
  },
  data() {
    return {
      newComment: null,
      isMinimize: this.minimizable && this.$vuetify.breakpoint.mdAndDown,
      skeletonComments: new Array(5).fill().map((a, i) => i),
      fileDialog: { dialog: false },
      uppyInstance: null
    };
  },
  computed: {
    ...mapState({
      self: state => state.userData
    }),
    commentsByDate() {
      return groupByDate({ items: this.comments });
    }
  },
  methods: {
    showFile(attachment) {
      this.fileDialog = {
        src: this.fileUrl(attachment.url),
        title: attachment.name,
        dialog: true
      };
    },
    fileUrl(url) {
      const token = `token=${getToken()}`;
      return `${url}?${token}`;
    },
    focusOnTextField() {
      this.$nextTick(() => this.$refs.commentField.$refs.input.focus());
    },
    isSelf(id) {
      return this.self.id === id;
    },
    addComment() {
      if (this.newComment) {
        this.$emit("comment", this.newComment);
        this.newComment = null;
      }
    },
    toggleMinimize() {
      if (this.minimizable) {
        this.isMinimize = !this.isMinimize;
      }
    },
    onResize() {
      if (this.$vuetify.breakpoint.mdAndDown && this.minimizable) {
        this.isMinimize = true;
      }
    },
    selectFile({ uppyInstance, file }) {
      this.uppyInstance = uppyInstance;
      this.$emit("selectFile", file);
    },
    unselectFile({ file }) {
      this.$emit("unselectFile", file);
      this.resetFileInput();
    },
    resetFileInput() {
      if (this.uppyInstance) {
        this.uppyInstance.reset();
      }
    },
    size
  },
  filters: {
    initials(author) {
      return getNameInitials(author);
    },
    timeForHumans(date) {
      return getTimeForHumans(date);
    },
    count(value) {
      return value.length;
    }
  }
};
</script>

<style lang="scss"></style>
